import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Clock, Star, Flame, Beef, Cookie, Droplet, Wheat, Utensils } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface RecipeDialogProps {
  meal: {
    name: string;
    description: string;
    cookingTime: number;
    recipe: {
      prepTime: number;
      cookTime: number;
      ingredients: string[];
      instructions: string[];
      tips?: string[];
      nutrition: {
        calories: number;
        protein: number;
        carbs: number;
        fat: number;
        fiber: number;
      };
    };
  };
  onToggleFavorite?: () => void;
  isFavorite?: boolean;
}

export function RecipeDialog({
  meal,
  onToggleFavorite,
  isFavorite = false,
}: RecipeDialogProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rtl-button-icon">
          צפה במתכון
          <Clock className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl" dir="rtl">
        <DialogHeader>
          <DialogTitle>{meal.name}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-6">
            {/* Recipe Overview */}
            <div className="space-y-4">
              <p className="text-muted-foreground">
                {meal.description}
              </p>

              <div className="flex items-center justify-between">
                <Button
                  variant={isFavorite ? "default" : "outline"}
                  size="sm"
                  onClick={onToggleFavorite}
                  className="rtl-button-icon"
                >
                  {isFavorite ? "מועדף" : "הוסף למועדפים"}
                  <Star
                    className={cn(
                      "h-4 w-4",
                      isFavorite && "fill-yellow-400 text-yellow-400",
                    )}
                  />
                </Button>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>זמן הכנה: {meal.recipe.prepTime} דקות</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Utensils className="h-4 w-4 text-muted-foreground" />
                    <span>זמן בישול: {meal.recipe.cookTime} דקות</span>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Ingredients */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">מרכיבים</h3>
              <ul className="space-y-2">
                {meal.recipe.ingredients.map((ingredient, index) => (
                  <li key={index} className="flex gap-4 flex-row-reverse">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                      {index + 1}
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm">
                        {ingredient}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <Separator />

            {/* Instructions */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">הוראות הכנה</h3>
              <ol className="space-y-6">
                {meal.recipe.instructions.map((instruction, index) => (
                  <li key={index} className="flex gap-4 flex-row-reverse">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium">
                      {index + 1}
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm leading-relaxed">
                        {instruction}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Tips */}
            {meal.recipe.tips && meal.recipe.tips.length > 0 && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">טיפים</h3>
                  <ul className="space-y-2">
                    {meal.recipe.tips.map((tip, index) => (
                      <li key={index} className="text-sm">
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            <Separator />

            {/* Nutrition */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                ערכים תזונתיים
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 [direction:rtl]">
                <Card>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-2">
                      <Flame className="h-4 w-4 text-red-500" />
                      <span>קלוריות</span>
                    </div>
                    <span className="font-semibold">{meal.recipe.nutrition.calories}</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-2">
                      <Beef className="h-4 w-4 text-purple-500" />
                      <span>חלבון</span>
                    </div>
                    <span className="font-semibold">{meal.recipe.nutrition.protein}ג</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-2">
                      <Cookie className="h-4 w-4 text-yellow-500" />
                      <span>פחמימות</span>
                    </div>
                    <span className="font-semibold">{meal.recipe.nutrition.carbs}ג</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-2">
                      <Droplet className="h-4 w-4 text-blue-500" />
                      <span>שומן</span>
                    </div>
                    <span className="font-semibold">{meal.recipe.nutrition.fat}ג</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-2">
                      <Wheat className="h-4 w-4 text-green-500" />
                      <span>סיבים</span>
                    </div>
                    <span className="font-semibold">{meal.recipe.nutrition.fiber}ג</span>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
